import { ChevronDown, CircleX } from 'lucide-react';
import { useForm } from 'react-hook-form';
import React, { memo, useMemo, useState, useRef, useEffect } from 'react';

const ProductFilterForm = ({
  categoryProducts,
  handleFilter,
  products,
  showDownpayment = true,
  showSorting = true,
}) => {
  const [openDropdown, setOpenDropdown] = useState(null);
  const [priceRange, setPriceRange] = useState([100, 300000]);
  const dropdownRef = useRef(null);

  const getUniqueOptions = (products, key) => {
    if (!Array.isArray(products)) return [];
    return [...new Set(products.map((product) => product?.[key]))].filter(
      Boolean
    );
  };

  const uniqueBrands = useMemo(() => {
    if (!Array.isArray(categoryProducts || products)) return [];

    const allProducts = categoryProducts || products;
    const brandMap = new Map();

    // First pass: collect all brands with normalized keys
    allProducts.forEach((product) => {
      const brand = product?.productBrandName;
      if (!brand) return;

      const normalizedBrand = brand.toLowerCase().trim();

      // Keep the shorter version (base brand name)
      if (
        !brandMap.has(normalizedBrand) ||
        brand.length < brandMap.get(normalizedBrand).length
      ) {
        brandMap.set(normalizedBrand, brand);
      }
    });

    // Second pass: remove brands that are subsets of other brands
    const allBrands = Array.from(brandMap.entries());
    const filteredBrands = [];

    allBrands.forEach(([normalizedKey, brandValue]) => {
      // Check if this brand is a prefix of any other brand
      const isSubBrand = allBrands.some(([otherKey, otherValue]) => {
        if (normalizedKey === otherKey) return false;
        // If another brand starts with this brand name (e.g., "samsung galaxy" starts with "samsung")
        return otherKey.startsWith(normalizedKey + ' ');
      });

      // If this brand is a base brand (others start with it), keep it
      // If this brand is a sub-brand (starts with another brand), skip it
      const hasBaseBrand = allBrands.some(([otherKey, otherValue]) => {
        if (normalizedKey === otherKey) return false;
        // Check if this brand starts with another brand (e.g., "samsung galaxy" starts with "samsung")
        return normalizedKey.startsWith(otherKey + ' ');
      });

      if (!hasBaseBrand) {
        filteredBrands.push(brandValue);
      }
    });

    // Return unique brands sorted alphabetically
    return filteredBrands.sort((a, b) =>
      a.toLowerCase().localeCompare(b.toLowerCase())
    );
  }, [categoryProducts, products]);

  // Get available downpayment percentages based on minimumDownPaymentPercentage
  const availableDownpayments = useMemo(() => {
    if (!Array.isArray(categoryProducts || products)) return [];

    const allProducts = categoryProducts || products;
    const percentages = allProducts
      .map((product) => {
        const percent = product?.minimumDownPaymentPercentage;
        if (!percent) return null;
        // Handle string format like "35.00" or number format
        const numericValue =
          typeof percent === 'string'
            ? parseInt(percent.split('.')[0])
            : parseInt(percent);
        return numericValue;
      })
      .filter((percent) => percent != null && percent > 0);

    if (percentages.length === 0) return [];

    // Get unique percentages and sort them
    const uniquePercentages = [...new Set(percentages)].sort((a, b) => a - b);

    // Format as percentage strings
    return uniquePercentages.map((percent) => `${percent}%`);
  }, [categoryProducts, products]);

  const sortOptions = [
    { value: 'newly-listed-desc', label: 'Newly listed (Newest first)' },
    { value: 'newly-listed-asc', label: 'Newly listed (Oldest first)' },
    { value: 'price-low-high', label: 'Price: Low to High' },
    { value: 'price-high-low', label: 'Price: High to Low' },
    { value: 'popular', label: 'Most Popular' },
  ];

  const { register, handleSubmit, watch, reset, setValue } = useForm({
    defaultValues: {
      brand: [],
      minPrice: 100,
      maxPrice: 300000,
      downpayment: '',
      sortBy: '',
    },
  });

  const values = watch();
  const filterCount = [
    values.brand?.length > 0,
    values.minPrice !== 100 || values.maxPrice !== 300000,
    values.downpayment,
    values.sortBy,
  ].filter(Boolean).length;

  // Auto-submit filters when values change
  useEffect(() => {
    if (handleFilter) {
      handleFilter(values);
    }
  }, [values, handleFilter]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setOpenDropdown(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const toggleDropdown = (dropdown) => {
    setOpenDropdown(openDropdown === dropdown ? null : dropdown);
  };

  const handleBrandChange = (brand) => {
    const currentBrands = values.brand || [];
    const newBrands = currentBrands.includes(brand)
      ? currentBrands.filter((b) => b !== brand)
      : [...currentBrands, brand];
    setValue('brand', newBrands);
  };

  const handlePriceChange = (index, value) => {
    const newRange = [...priceRange];
    newRange[index] = Number(value);
    setPriceRange(newRange);
    setValue('minPrice', newRange[0]);
    setValue('maxPrice', newRange[1]);
  };

  const handleResetFilter = () => {
    reset();
    setPriceRange([100, 300000]);
    setOpenDropdown(null);
  };

  return (
    <div className="relative my-5">
      {/* Main container - column on mobile, row on desktop */}
      <div className="flex flex-col md:flex-row md:items-center gap-3">
        {/* Filter buttons row - scrollable on mobile, wrapped on desktop */}
        <div className="flex items-center gap-3 md:flex-wrap overflow-x-auto md:overflow-x-visible scrollbar-hide pb-2 md:pb-0">
          <span className="text-sm font-medium text-gray-700 flex-shrink-0">
            Filter
          </span>

          {/* Brand Dropdown */}
          <div
            className="relative flex-shrink-0"
            ref={openDropdown === 'brand' ? dropdownRef : null}
          >
            <button
              type="button"
              onClick={() => toggleDropdown('brand')}
              className="flex items-center gap-2 px-4 py-2 rounded-full border border-black bg-[#F7F7F7] text-sm hover:border-gray-300 transition-colors whitespace-nowrap"
            >
              <span>Brand</span>
              <ChevronDown
                size={16}
                className={`transition-transform ${openDropdown === 'brand' ? 'rotate-180' : ''}`}
              />
            </button>

            {openDropdown === 'brand' && (
              <div className="fixed md:absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 md:top-full md:left-0 md:translate-x-0 md:translate-y-0 md:mt-2 w-[90vw] max-w-[280px] bg-white rounded-2xl shadow-lg border border-gray-200 p-6 z-50">
                <h3 className="text-sm font-semibold mb-2">Brand</h3>
                <hr className="my-3" />
                <div className="max-h-[300px] overflow-y-auto space-y-1">
                  {uniqueBrands.map((brand, index) => (
                    <label
                      key={index}
                      className="flex items-center gap-3 cursor-pointer hover:bg-gray-50 p-1 rounded"
                    >
                      <input
                        type="checkbox"
                        checked={values.brand?.includes(brand)}
                        onChange={() => handleBrandChange(brand)}
                        className="w-4 h-4 rounded border-gray-300"
                      />
                      <span className="text-sm">{brand}</span>
                    </label>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Price Dropdown */}
          <div
            className="relative flex-shrink-0"
            ref={openDropdown === 'price' ? dropdownRef : null}
          >
            <button
              type="button"
              onClick={() => toggleDropdown('price')}
              className="flex items-center gap-2 px-4 py-2 rounded-full border border-black bg-[#F7F7F7] text-sm hover:border-gray-300 transition-colors whitespace-nowrap"
            >
              <span>Price</span>
              <ChevronDown
                size={16}
                className={`transition-transform ${openDropdown === 'price' ? 'rotate-180' : ''}`}
              />
            </button>

            {openDropdown === 'price' && (
              <div className="fixed md:absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 md:top-full md:left-0 md:translate-x-0 md:translate-y-0 md:mt-2 w-[90vw] max-w-[380px] bg-white rounded-xl shadow-xl border border-gray-200 p-5 z-50">
                <h3 className="text-base font-semibold mb-2 text-gray-800">
                  Choose price
                </h3>
                <hr className="my-4" />

                <div className="flex gap-3 mb-5">
                  <div className="flex-1">
                    <label className="text-xs font-medium text-gray-600 mb-1.5 block">
                      ₦ Min
                    </label>
                    <input
                      type="number"
                      value={priceRange[0]}
                      onChange={(e) => handlePriceChange(0, e.target.value)}
                      className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-yellow-400 focus:border-transparent"
                      placeholder="100"
                    />
                  </div>
                  <div className="flex-1">
                    <label className="text-xs font-medium text-gray-600 mb-1.5 block">
                      ₦ Max
                    </label>
                    <input
                      type="number"
                      value={priceRange[1]}
                      onChange={(e) => handlePriceChange(1, e.target.value)}
                      className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-yellow-400 focus:border-transparent"
                      placeholder="300000"
                    />
                  </div>
                </div>

                <div>
                  <div className="flex justify-between text-xs font-medium text-gray-600 mb-3">
                    <span>₦{priceRange[0].toLocaleString()}</span>
                    <span>₦{priceRange[1].toLocaleString()}</span>
                  </div>
                  <div className="relative">
                    <input
                      type="range"
                      min="100"
                      max="300000"
                      value={priceRange[1]}
                      onChange={(e) => handlePriceChange(1, e.target.value)}
                      className="w-full h-2 bg-gray-200 rounded-full appearance-none cursor-pointer"
                      style={{
                        background: `linear-gradient(to right, #FFDE11 0%, #FFDE11 ${((priceRange[1] - 100) / 299900) * 100}%, #E5E7EB ${((priceRange[1] - 100) / 299900) * 100}%, #E5E7EB 100%)`,
                      }}
                    />
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Sorting Dropdown */}
          {showSorting && (
            <div
              className="relative flex-shrink-0"
              ref={openDropdown === 'sort' ? dropdownRef : null}
            >
              <button
                type="button"
                onClick={() => toggleDropdown('sort')}
                className="flex items-center gap-2 px-4 py-2 rounded-full border border-black bg-[#F7F7F7] text-sm hover:border-gray-300 transition-colors whitespace-nowrap"
              >
                <span>
                  {values.sortBy
                    ? sortOptions.find((o) => o.value === values.sortBy)?.label
                    : 'Sort by'}
                </span>
                <ChevronDown
                  size={16}
                  className={`transition-transform ${openDropdown === 'sort' ? 'rotate-180' : ''}`}
                />
              </button>

              {openDropdown === 'sort' && (
                <div className="fixed md:absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 md:top-full md:left-0 md:translate-x-0 md:translate-y-0 md:mt-2 w-[90vw] max-w-[220px] bg-white rounded-2xl shadow-lg border border-gray-200 py-2 z-50">
                  {sortOptions.map((option) => (
                    <button
                      key={option.value}
                      type="button"
                      onClick={() => {
                        setValue('sortBy', option.value);
                        setOpenDropdown(null);
                      }}
                      className="w-full text-left px-4 py-2 text-sm hover:bg-gray-50 transition-colors"
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Downpayment Dropdown */}
          {showDownpayment && availableDownpayments.length > 0 && (
            <div
              className="relative flex-shrink-0 md:mr-6"
              ref={openDropdown === 'downpayment' ? dropdownRef : null}
            >
              <button
                type="button"
                onClick={() => toggleDropdown('downpayment')}
                className="flex items-center gap-2 px-4 py-2 rounded-full border border-black bg-[#F7F7F7] text-sm hover:border-gray-300 transition-colors whitespace-nowrap"
              >
                <span>{values.downpayment || 'Downpayment'}</span>
                <ChevronDown
                  size={16}
                  className={`transition-transform ${openDropdown === 'downpayment' ? 'rotate-180' : ''}`}
                />
              </button>

              {openDropdown === 'downpayment' && (
                <div className="fixed md:absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 md:top-full md:left-0 md:translate-x-0 md:translate-y-0 md:mt-2 w-[90vw] max-w-[360px] bg-white rounded-xl shadow-xl border border-gray-200 p-5 z-50">
                  <h3 className="text-base font-semibold mb-2 text-gray-800">
                    Downpayment
                  </h3>
                  <hr className="my-4" />
                  {availableDownpayments.length > 0 ? (
                    <div className="flex flex-wrap gap-2.5">
                      {availableDownpayments.map((percentage) => (
                        <button
                          key={percentage}
                          type="button"
                          onClick={() => {
                            setValue('downpayment', percentage);
                            setOpenDropdown(null);
                          }}
                          className={`p-2 rounded-lg text-sm font-medium transition-all ${
                            values.downpayment === percentage
                              ? 'bg-black text-white shadow-md'
                              : 'bg-gray-100 text-gray-700 hover:bg-gray-200 border border-gray-200'
                          }`}
                        >
                          {percentage}
                        </button>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-gray-500 py-2">
                      No downpayment options available for these products.
                    </p>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Filter Count & Clear - Desktop only (hidden on mobile) */}
          <div className="hidden md:flex items-center gap-3 pl-4 border-l border-gray-200">
            <div className="flex items-center gap-2 px-4 py-2 rounded-full border-2 border-gray-200 bg-white text-sm">
              <span>
                {filterCount === 0
                  ? 'No filters applied'
                  : `${filterCount} filter${filterCount > 1 ? 's' : ''} applied`}
              </span>
              {filterCount > 0 && (
                <button
                  type="button"
                  onClick={handleResetFilter}
                  className="ml-1"
                  title="Clear all filters"
                  aria-label="Clear filters"
                >
                  <CircleX size={16} className="cursor-pointer" />
                </button>
              )}
            </div>

            <button
              type="button"
              onClick={handleResetFilter}
              className="px-4 py-2 rounded-full bg-black text-white text-sm hover:bg-gray-800 transition-colors"
            >
              Clear filter
            </button>
          </div>
        </div>

        {/* Filter Count & Clear - Mobile only (separate row) */}
        <div className="flex md:hidden items-center gap-3">
          <div className="flex items-center gap-2 px-4 py-2 rounded-full border-2 border-gray-200 bg-white text-sm">
            <span>
              {filterCount === 0
                ? 'No filters applied'
                : `${filterCount} filter${filterCount > 1 ? 's' : ''} applied`}
            </span>
            {filterCount > 0 && (
              <button
                type="button"
                onClick={handleResetFilter}
                className="ml-1"
                title="Clear all filters"
                aria-label="Clear filters"
              >
                <CircleX size={16} className="cursor-pointer" />
              </button>
            )}
          </div>

          <button
            type="button"
            onClick={handleResetFilter}
            className="px-4 py-2 rounded-full bg-black text-white text-sm hover:bg-gray-800 transition-colors"
          >
            Clear filter
          </button>
        </div>
      </div>

      {/* Backdrop overlay for mobile dropdowns */}
      {openDropdown && (
        <div
          className="fixed inset-0 bg-black/50 z-40 md:hidden"
          onClick={() => setOpenDropdown(null)}
        />
      )}
    </div>
  );
};

export default memo(ProductFilterForm);
